import React, { useMemo } from 'react';
import { Descriptions, Modal, Table, Tabs, Spin, Image } from 'antd';
import { useCurrentOperator } from '../hooks/useCurrentOperator';
import { useGetCouponsByOperatorIdQuery } from '../../../GraphQL/hooks/useGetCouponsByOperatorIdQuery';
import { CheckCircleOutlined, CloseCircleOutlined } from '@ant-design/icons';
import { convertDateToFormat } from '../../../shared/Utils';

const OperatorViewModal = ({ isOpen, onRequestClose }) => {
    const { currentOperator } = useCurrentOperator();
    const handleCancel = () => onRequestClose(false);

    const request = useMemo(() => ({ search: '' }), []);
    const { data: coupons, loading } = useGetCouponsByOperatorIdQuery(request, currentOperator?._id ?? '');

    const couponColumns = [
        { title: 'ID', dataIndex: 'logicalId', key: 'logicalId', width: 70 },
        { title: 'Title', dataIndex: 'title', key: 'title' },
        { title: 'Code', dataIndex: 'code', key: 'code' },
        { title: 'Type', dataIndex: 'type', key: 'type' },
        {
            title: 'Validity',
            key: 'dates',
            render: (_, record) => (
                <>
                    {convertDateToFormat(record.startingDate, 'DD-MM-YYYY')} →{' '}
                    {convertDateToFormat(record.expirationDate, 'DD-MM-YYYY')}
                </>
            ),
        },
        {
            title: 'Status',
            dataIndex: 'status',
            key: 'status',
            render: (status: boolean) => {
                return status === true ? (
                    <span style={{ fontWeight: 'bold', color: '#389e0d' }}>
                        <CheckCircleOutlined /> Active
                    </span>
                ) : (
                    <span style={{ color: '#8c8c8c' }}>
                        <CloseCircleOutlined /> Inactive
                    </span>
                );
            },
        },
    ];

    if (!currentOperator) return null;

    return (
        <Modal
            width={700}
            open={isOpen}
            onCancel={handleCancel}
            footer={null}
            title={`Operator details #${currentOperator?.logicalId}`}
        >
            <Tabs
                defaultActiveKey="1"
                items={[
                    {
                        key: '1',
                        label: 'Information',
                        children: (
                            <Descriptions bordered>
                                <Descriptions.Item span={3} label="Description">
                                    {currentOperator?.description}
                                </Descriptions.Item>
                                <Descriptions.Item span={3} label="TVA">
                                    {currentOperator?.tva}
                                </Descriptions.Item>
                                <Descriptions.Item span={3} label="Address">
                                    Country: {currentOperator?.address?.country}
                                    <br />
                                    Governorate: {currentOperator?.address?.governorate}
                                    <br />
                                    Postal code: {currentOperator?.address?.postalCode}
                                    <br />
                                    Street: {currentOperator?.address?.street}
                                </Descriptions.Item>
                                <Descriptions.Item span={3} label="Logo">
                                    <Image width={150} src={currentOperator?.logo?.url} />
                                </Descriptions.Item>
                            </Descriptions>
                        ),
                    },
                    {
                        key: '2',
                        label: `${currentOperator?.name}’s Coupons Info`,
                        children: (
                            <Spin spinning={loading}>
                                <Table
                                    rowKey="_id"
                                    columns={couponColumns}
                                    dataSource={coupons ?? []}
                                    pagination={{ pageSize: 5 }}
                                />
                            </Spin>
                        ),
                    },
                ]}
            />
        </Modal>
    );
};

export default OperatorViewModal;
